import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { Trans } from '@lingui/react';
import { NavStepper } from '../../components/steppers/nav-stepper/NavStepper';
import { NavStepperStep } from '../../components/steppers/nav-stepper/NavStepperStep';
import { GqlSeverity } from '../../graphql/base-types.graphql';
import { useArticlesOfAssocSuspenseQuery } from './ArticlesOfAssoc.graphql';
import { AoaStepMeeting } from './components/steps/StepMeeting';
import { useForm } from 'react-hook-form';
import { useAoaUpdateMutation } from '../../graphql/react-operations';
import { useTransition } from 'react';
import { StepSplit } from './components/steps/StepSplit';
import { StepShareLimits } from './components/steps/StepShareLimits';
import { BvContactForm } from '../../components/BvContactForm';
import { FormData } from './types';
import { StepDocument } from './components/steps/StepDocument';
import { StepKindOfBusiness } from './components/steps/StepKindOfBusiness';
import { StepSeat } from './components/steps/StepSeat';
import { StepSummons } from './components/steps/StepSummons';
import { StepFiscalYear } from './components/steps/StepFiscalYear';
import { StepBoardLimits } from './components/steps/StepBoardLimits';
import { StepReservations } from './components/steps/StepReservations';

export const ArticlesOfAssocEdit = ({ aoaId }: { aoaId: string }) => {
  const companyId = useCompanyId();
  const [pending, startTransition] = useTransition();
  const [save, { loading: updateLoading, error }] = useAoaUpdateMutation();

  const loading = pending || updateLoading;

  const {
    data: { articlesOfAssoc, companySubscription, articlesOfAssocErrors },
    refetch,
  } = useArticlesOfAssocSuspenseQuery({ variables: { companyId, articlesOfAssocId: aoaId } });

  const form = useForm<FormData>({
    defaultValues: {
      ...articlesOfAssoc,
      seat: articlesOfAssoc.seat ?? '',
      kindOfBusiness: articlesOfAssoc.kindOfBusiness ?? '',
    },
  });

  const onSave = (stepNext: () => Promise<unknown>) => async (input: FormData) => {
    await save({
      variables: {
        companyId,
        input: {
          ...input,
          id: aoaId,
        },
      },
    });
    await stepNext();
  };

  return (
    <Stack gap={3}>
      <Stack direction="row" gap={3} alignItems="center">
        <Typography level="h3">
          <Trans id="Articles of Association" />
        </Typography>
      </Stack>

      <NavStepper
        path="*"
        onStepSaved={() => {
          startTransition(() => {
            void refetch();
          });
        }}
      >
        <NavStepperStep
          path="decision"
          label={<Trans id="Decision" />}
          validatorStatus={articlesOfAssocErrors.decision}
          showTitle
        >
          <AoaStepMeeting
            articlesOfAssoc={articlesOfAssoc}
            meeting={articlesOfAssoc.meetingConcern?.meeting}
            hasActiveSubscription={companySubscription?.isActive ?? false}
          />
        </NavStepperStep>
        <NavStepperStep
          path="business"
          label={<Trans id="Business" />}
          validatorStatus={articlesOfAssocErrors.business}
          showTitle
        >
          {({ stepNext }) => <StepKindOfBusiness form={form} onSave={onSave(stepNext)} loading={loading} />}
        </NavStepperStep>
        <NavStepperStep path="seat" label={<Trans id="Seat" />} validatorStatus={articlesOfAssocErrors.seat} showTitle>
          {({ stepNext }) => <StepSeat form={form} onSave={onSave(stepNext)} loading={loading} />}
        </NavStepperStep>
        <NavStepperStep
          path="summons"
          label={<Trans id="Summons" />}
          validatorStatus={articlesOfAssocErrors.summons}
          showTitle
        >
          {({ stepNext }) => <StepSummons form={form} onSave={onSave(stepNext)} loading={loading} />}
        </NavStepperStep>
        <NavStepperStep
          path="fiscal-year"
          label={<Trans id="Fiscal year" />}
          validatorStatus={articlesOfAssocErrors.fiscalYear}
          showTitle
        >
          {({ stepNext }) => (
            <StepFiscalYear aoa={articlesOfAssoc} form={form} onSave={onSave(stepNext)} loading={loading} />
          )}
        </NavStepperStep>
        <NavStepperStep
          path="number-of-shares"
          label={<Trans id="Number of shares" />}
          validatorStatus={articlesOfAssocErrors.shareChange}
          showTitle
        >
          {({ stepNext }) => (
            <StepSplit
              loading={loading}
              stepNext={stepNext}
              aoa={articlesOfAssoc}
              onSave={data =>
                onSave(stepNext)({
                  numberOfSharesAfter: data.numberOfSharesAfter,
                  skipConcernUpdates: data.skipConcernUpdates,
                })
              }
            />
          )}
        </NavStepperStep>
        <NavStepperStep
          path="share-limits"
          label={<Trans id="Share limits" />}
          validatorStatus={articlesOfAssocErrors.shareLimit}
          showTitle
        >
          {({ stepNext }) => (
            <StepShareLimits
              stepNext={stepNext}
              numberOfSharesAfter={articlesOfAssoc.numberOfSharesAfter ?? articlesOfAssoc.numberOfSharesBefore ?? ''}
              changeShareLimits={articlesOfAssoc.changeShareLimits ?? false}
              quotaValueAfter={articlesOfAssoc.quotaAfter ?? ''}
              minimumNumberOfShares={articlesOfAssoc.minimumNumberOfShares ?? ''}
              maximumNumberOfShares={articlesOfAssoc.maximumNumberOfShares ?? ''}
              minimumShareCapital={articlesOfAssoc.minimumShareCapital ?? ''}
              maximumShareCapital={articlesOfAssoc.maximumShareCapital ?? ''}
              onSave={data => onSave(stepNext)(data)}
            />
          )}
        </NavStepperStep>
        <NavStepperStep
          path="board-limits"
          label={<Trans id="Board limits" />}
          validatorStatus={articlesOfAssocErrors.board}
          showTitle
        >
          {({ stepNext }) => <StepBoardLimits form={form} onSave={onSave(stepNext)} loading={loading} />}
        </NavStepperStep>

        <NavStepperStep
          path="reservation"
          label={<Trans id="Reservations" />}
          validatorStatus={articlesOfAssocErrors.reservations}
          showTitle
        >
          {({ stepNext }) => <StepReservations form={form} onSave={onSave(stepNext)} loading={loading} />}
        </NavStepperStep>

        <NavStepperStep
          path="registration-form"
          label={<Trans id="Registration form" />}
          validatorStatus={articlesOfAssocErrors.signsRegForm}
          showTitle
        >
          {({ stepNext }) => (
            <BvContactForm
              onSave={data =>
                onSave(stepNext)({
                  regFormPaymentThroughBolago: data.regFormPaymentThroughBolago,
                  contact: data.contact,
                  signatory: data.signatory,
                })
              }
              loading={loading}
              error={error}
              defaultValues={articlesOfAssoc}
            />
          )}
        </NavStepperStep>
        <NavStepperStep
          path="documents"
          label={<Trans id="Documents" />}
          validatorStatus={{
            completed: Boolean(articlesOfAssoc.latestDocument),
            hasError: false,
            severity: GqlSeverity.Error,
          }}
        >
          <StepDocument id={articlesOfAssoc.id} />
        </NavStepperStep>
      </NavStepper>
    </Stack>
  );
};
